import { useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import {
  Bell,
  Key,
  Home,
  CreditCard,
  Wallet,
  BarChart3,
  Link,
  BookOpen,
  Users as UsersIcon,
  Menu,
  User,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { ROUTES } from '../../../config/routes'
import { useAuth } from '../../../lib/auth.context'
import { useMerchant } from '../../../lib/hooks/useMerchant'
import { useTheme } from '../../../lib/theme-context'
import { useIsMobile } from '../../../hooks/use-mobile'

type MenuItem = {
  title: string
  icon: LucideIcon
  href: string
  comingSoon?: boolean
}

const getMenuItems = (): MenuItem[] => [
  {
    title: 'Dashboard',
    icon: Home,
    href: ROUTES.MERCHANT.DASHBOARD,
  },
  {
    title: 'Transactions',
    icon: CreditCard,
    href: ROUTES.MERCHANT.TRANSACTIONS,
  },
  {
    title: 'Payouts',
    icon: Wallet,
    href: ROUTES.MERCHANT.PAYOUTS,
  },
  {
    title: 'Users',
    icon: UsersIcon,
    href: ROUTES.MERCHANT.USERS,
  },
  {
    title: 'Profile',
    icon: User,
    href: ROUTES.MERCHANT.PROFILE,
  },
  // {
  //   title: 'Reports',
  //   icon: BarChart3,
  //   href: '#',
  //   comingSoon: true,
  // },
  // {
  //   title: 'API Keys',
  //   icon: Key,
  //   href: ROUTES.MERCHANT.API_KEYS,
  // },
  // {
  //   title: 'Webhooks',
  //   icon: Link,
  //   href: ROUTES.MERCHANT.WEBHOOKS,
  // },
  // {
  //   title: 'Notifications',
  //   icon: Bell,
  //   href: '#',
  //   comingSoon: true,
  // },
  // {
  //   title: 'Documentation',
  //   icon: BookOpen,
  //   href: ROUTES.MERCHANT.DOCUMENTATION,
  // },
]

const lightLogo = '/fanfair.png'
const darkLogo = '/fanfairreversed.png'

interface SidebarProps {
  collapsed: boolean
  onToggle: () => void
}

// export function SidebarNav() {
export const SidebarNav = ({ collapsed, onToggle }: SidebarProps) => {
  const [imageError, setImageError] = useState(false)
  const { theme, setTheme } = useTheme()
  const location = useLocation()
  const { status } = useAuth()
  const { merchantId } = useMerchant()
  const isAuthenticated = status === 'authenticated'
  const isMobile = useIsMobile()

  // Determine which logo to use based on current theme
  const currentTheme = theme === 'light' ? setTheme : theme
  const logoSrc = currentTheme === 'dark' ? darkLogo : lightLogo

  if (isMobile) {
    collapsed = true
  }

  // Log navigation state for debugging
  console.log('[SidebarNav] Current path:', location.pathname)
  console.log('[SidebarNav] isAuthenticated:', isAuthenticated)

  return (
    <div
      className={`fixed left-0 top-0 h-full bg-card border-r border-border transition-all duration-300 z-10 ${
        collapsed ? 'w-16' : 'w-sidebar'
      }`}
    >
      {/* Logo Section */}
      <div className="h-header flex items-center justify-between px-4 border-b border-border">
        <NavLink to="/dashboard" className="flex items-center">
          {!collapsed && (
            <div className="flex items-center gap-2">
              {!imageError ? (
                <img
                  src={logoSrc}
                  alt="FanFair"
                  className="h-8 w-auto"
                  onError={(e) => {
                    console.error('Logo failed to load:', e.currentTarget.src)
                    setImageError(true)
                  }}
                  onLoad={() => console.log('Logo loaded successfully:', logoSrc)}
                />
              ) : (
                <div className="text-lg font-bold text-primary">FanFair</div>
              )}
            </div>
          )}
        </NavLink>
        <button
          onClick={onToggle}
          className="hidden md:block p-2 mt-1 hover:bg-accent rounded-lg transition-colors"
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <Menu className="h-5 w-5" />
        </button>
      </div>
      <nav className="p-4">
        <div className="space-y-2">
          {getMenuItems().map((item) => {
            const Icon = item.icon
            const baseClasses = `fanfair-sidebar-item ${collapsed ? 'justify-center px-3' : 'justify-start'}`
            return item.comingSoon ? (
              <div
                key={item.title}
                aria-disabled="true"
                className={`${baseClasses} cursor-not-allowed opacity-60 text-muted-foreground`}
                onClick={(e) => e.preventDefault()}
              >
                <Icon className="h-5 w-5 flex-shrink-0" />
                {!collapsed && (
                  <div className="flex items-center gap-2">
                    <span>{item.title}</span>
                    <span className="text-xs rounded bg-muted px-2 py-0.5 text-muted-foreground">Coming soon</span>
                  </div>
                )}
              </div>
            ) : (
              <NavLink
                key={item.title}
                to={item.href}
                // end={item.href === '/'}
                className={({ isActive }) => `${baseClasses} ${isActive ? 'active' : ''}`}
              >
                <Icon className="h-5 w-5 flex-shrink-0" />
                {!collapsed && <span>{item.title}</span>}
              </NavLink>
            )
          })}
        </div>
      </nav>
    </div>
  )
}
